import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { Separator, Button, Text, Loader, Tab, Badge } from '@/ui'
import { PracticeInformationCard } from './PracticeInformationCard'
import { UserTransactionsCard } from './UserTransactionsCard'
import { BookkeepingReportsCard } from './BookkeepingReportsCard'
import FinancialAccountsCard from './FinancialAccountsCard'
import { TaxTabPanel } from '@/features/admin/user-record/TaxTabPanel'
import PayrollTabPanel from '@/features/admin/user-record/payroll/PayrollTabPanel'
import { AllocationGuidesCard } from './AllocationGuidesCard'
import { UserActionItemsPanel } from './UserActionItemsPanel'
import {
  adminIsUserNoServiceFreeTrial,
  getUserById,
  selectAdminUserState,
} from '../../../selectors/user.selectors'
import { adminFetchSingleUser } from '../../../actions/admin/adminAllUsersActions'
import { fetchAdminTransactionCategoriesIfNeeded } from '@/features/reports/reports.slice'
import { fetchAllTransactionRulesIfNeeded } from '../../../actions/admin/transactionRuleActions'
import { fetchAdminUserDocumentCategoriesIfNeeded } from '@/features/admin/user-document-categories/userDocumentCategories.slice'

import { SmartRuleCreation } from '@/features/admin/smart-rule-creation/index'
import './UserRecord.scss'
import { Documents } from '@/features/admin/documents/index'
import { UserBreadcrumbs } from './UserBreadcrumbs'
import { AdminUserCallsCard } from '@/features/admin/user-record/AdminUserCallsCard'
import { FinancialReportsCard } from './FinancialReportsCard'
import { FinancialAccountReconciliationsCard } from './FinancialAccountReconciliationsCard'
import { AccountDetailsTabPanel } from '@/features/admin/user-record/AccountDetailsTabPanel'
import { fetchAdminUsersDocuments } from '@/actions/admin/userDocumentsActions'
import { useReselector, useHashScroll } from '@/utils/sharedHooks'
import { UserRecordNotes } from './UserRecordNotes'
import { UserWithAdminInfo } from '@/reducers/admin/allUsersReducer'
import { selectNumberUserDocumentsNeedsReview } from '@/features/user/your-practice/documents/lib/userDocuments.selector'
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { selectAdminAnnualTaxFilingsByYearForUser } from '@/features/admin/taxes/adminAnnualTaxFilings.selector'
import { fetchAdminAnnualTaxFilings } from '@/features/admin/taxes/adminAnnualTaxFilings.slice'
import { SCorpPanel } from '@/features/admin/user-record/SCorpPanel'
import { UserAccountCanceledAlert } from './UserAccountStates/UserAccountCanceledAlert'
import { UserStateLabel } from './UserAccountStates/UserStateLabel'
import { useAppDispatch } from '@/utils/typeHelpers'
import { FinancialAdvisoryPanel } from '@/features/admin/user-record/FinancialAdvisoryPanel'
import SubmissionsPanel from '@/features/bookkeeping/end-of-year-review/admin/submissions-panel'
import AdminUserCouponAssignment from '@/features/user-coupons/AdminUserCouponAssignment'
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons'
import { TypographyStyles } from '@/styles'
import { BookLockCard } from './BookLockCard'
import { BankingProfilePanel } from './BankingProfilePanel'
import { UserFreeTrialAlert } from './UserAccountStates/UserFreeTrialAlert'

const UserRecordHeaderTaxJoinerStatus = ({
  user,
}: {
  user: UserWithAdminInfo
}) => {
  const dispatch = useAppDispatch()
  const taxYear = useReselector(selectCurrentAnnualTaxYear)
  const pageLimitAndLengthForSingleUser = 1

  useEffect(() => {
    fetchAdminAnnualTaxFilings({
      page: pageLimitAndLengthForSingleUser,
      limit: pageLimitAndLengthForSingleUser,
      year: taxYear,
      userIds: user.id,
    })(dispatch)
  }, [dispatch, taxYear, user.id])

  const annualTaxFiling = useReselector(
    selectAdminAnnualTaxFilingsByYearForUser,
    taxYear,
    user.id
  )

  if (annualTaxFiling?.isLateJoiner) {
    return <Badge color="red">{Number(taxYear) + 1} Late Joiner</Badge>
  } else {
    return null
  }
}
const UserRecordHeader = ({ user }: { user: UserWithAdminInfo }) => {
  const userState = useReselector(selectAdminUserState, user.id)

  if (!userState) {
    return null
  }

  return (
    <div className="flex items-center justify-between py-3 userRecordHeader">
      <div className="flex items-center gap-4">
        <Link to="/admin/finances/list-users">
          <Button variant="ghost" iconLeft={faArrowLeft} />
        </Link>
        <Text
          variant={TypographyStyles.H3}
          className={userState === 'unpaid' ? 'text-red-600' : ''}
        >
          {user?.firstName} {user?.lastName}
        </Text>
        <div className="flex items-center gap-2">
          <UserStateLabel user={user} />
          <UserRecordHeaderTaxJoinerStatus user={user} />
          {/* TEMP CORE-9199 remove after Oct 30 */}
          <Link to={`/admin/finances/records/${user.id}/shadow`}>
            <Button variant="secondary">Shadow this session</Button>
          </Link>
        </div>
      </div>
    </div>
  )
}

const UserInfoPanel = ({ user }: { user: UserWithAdminInfo }) => (
  <div className="clientRecordPanel left">
    <PracticeInformationCard user={user} />
    <BookLockCard user={user} />
    <UserRecordNotes userId={user.id} />
    <BookkeepingReportsCard user={user} />
    <AllocationGuidesCard user={user} />
    <FinancialAccountsCard user={user} />
    <UserTransactionsCard user={user} />
    <AdminUserCallsCard userId={user.id} />
    <FinancialAccountReconciliationsCard userId={user.id} />
  </div>
)

const UserRecord = () => {
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(true)
  const { userId: userIdString } = useParams<{ userId: string }>()
  const [searchParams] = useSearchParams()
  const userId = Number(userIdString)
  const user = useReselector(getUserById, userId)
  const isUnservicedFreeTrial = useReselector(
    adminIsUserNoServiceFreeTrial,
    user?.id
  )
  const numNeedsReview = useReselector(
    selectNumberUserDocumentsNeedsReview,
    userId
  )

  useEffect(() => {
    const fetch = async () => {
      await Promise.all([
        dispatch(adminFetchSingleUser(userId)),
        dispatch(fetchAdminTransactionCategoriesIfNeeded()),
        dispatch(fetchAllTransactionRulesIfNeeded()),
        dispatch(fetchAdminUserDocumentCategoriesIfNeeded()),
        dispatch(fetchAdminUsersDocuments(userId)),
      ])

      setLoading(false)
    }

    fetch()
  }, [dispatch, userId])

  useHashScroll(!loading)

  return (
    <div id="userRecord">
      <UserBreadcrumbs userId={userId} />
      <Separator />

      {user && <UserRecordHeader user={user} />}
      {(loading || !user) && <Loader active>Fetching</Loader>}
      {!loading && user && <UserAccountCanceledAlert user={user} />}
      {!loading && user && <UserFreeTrialAlert userId={user.id} />}
      <br />
      {!loading && user && (
        <Tab
          variant="default"
          fullWidth
          scrollable
          defaultActiveIndex={searchParams.get('tab') === 'documents' ? 5 : 0}
          panes={[
            {
              menuItem: 'User Record',
              render: () => <UserInfoPanel user={user} />,
            },
            {
              menuItem: 'Quarterly Tax Estimates',
              render: () => (
                <div className="splitPanelWrapper">
                  <TaxTabPanel userId={userId} />
                </div>
              ),
            },

            {
              menuItem: 'Financial Statements',
              render: () => (
                <div className="w-full">
                  <FinancialReportsCard userId={user.id} />
                </div>
              ),
            },
            {
              menuItem: `Documents (${numNeedsReview})`,
              render: () => (
                <div className="w-full">
                  <Documents user={user} />
                </div>
              ),
            },
            {
              menuItem: 'Payroll',
              render: () => (
                <div className="w-full">
                  <PayrollTabPanel userId={userId} />
                </div>
              ),
            },
            {
              menuItem: 'Account Details',
              render: () => (
                <AccountDetailsTabPanel
                  userId={userId}
                  isUnservicedFreeTrial={isUnservicedFreeTrial}
                />
              ),
            },
            {
              menuItem: 'Submissions',
              render: () => (
                <div className="w-full">
                  <SubmissionsPanel userId={userId} />
                </div>
              ),
            },
            {
              menuItem: 'Action Items',
              render: () => (
                <div className="w-full">
                  <UserActionItemsPanel user={user} />
                </div>
              ),
            },
            {
              menuItem: 'Transaction Rules',
              render: () => (
                <div className="w-full">
                  <SmartRuleCreation user={user} />
                </div>
              ),
            },
            {
              menuItem: 'Miscellaneous',
              render: () => (
                <div className="flex flex-col gap-4">
                  <div className="splitPanelWrapper">
                    <SCorpPanel userId={userId} />
                  </div>
                  <div className="splitPanelWrapper">
                    <FinancialAdvisoryPanel userId={userId} />
                  </div>
                  <div className="splitPanelWrapper">
                    <AdminUserCouponAssignment userId={userId} />
                  </div>
                  <div className="splitPanelWrapper">
                    <BankingProfilePanel userId={userId} />
                  </div>
                </div>
              ),
            },
          ]}
        />
      )}
    </div>
  )
}

export default UserRecord
